import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useUser } from '../contexts/UserContext';
import './UsageLimitModal.css';

// Tier definitions mirroring PricingPage.js
const TIERS = [
  { name: 'free', displayName: 'Free', price: 0, screener: '50', watchlist: '50', savedParams: '5' },
  { name: 'essential', displayName: 'Essential', price: 9, screener: '500', watchlist: '500', savedParams: '25' },
  { name: 'plus', displayName: 'Plus', price: 24, screener: '2,000', watchlist: '2,000', savedParams: '100' },
  { name: 'premium', displayName: 'Premium', price: 49, screener: 'Unlimited', watchlist: 'Unlimited', savedParams: 'Unlimited' },
];

const TIER_ORDER = ['free', 'essential', 'plus', 'premium'];

function UsageLimitModal({ data, onClose }) {
  const navigate = useNavigate();
  const { tier } = useUser();
  const currentTier = tier || 'free';

  if (!data) return null;

  const { toolName, otherToolName, otherToolRoute, used, limit } = data;

  // Get tiers above the user's current tier
  const currentIndex = TIER_ORDER.indexOf(currentTier);
  const upgradeTiers = TIERS.filter((_, i) => i > currentIndex);

  // Get the limit field key for the exceeded usage type
  const limitKey = data.usageType === 'screener_contracts'
    ? 'screener'
    : data.usageType === 'saved_parameters'
      ? 'savedParams'
      : 'watchlist';

  // Determine labels based on whether this is a monthly limit or a hard cap
  const isMonthly = data.usageType !== 'saved_parameters';
  const unitLabel = data.usageType === 'saved_parameters'
    ? 'saved parameters'
    : `${toolName.toLowerCase()} contracts`;
  const tierUnit = data.usageType === 'saved_parameters' ? 'parameters' : 'contracts';

  const handleViewPlans = () => {
    onClose();
    navigate('/pricing');
  };

  const handleTryOtherTool = () => {
    onClose();
    navigate(otherToolRoute);
  };

  const handleOverlayClick = (e) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  // Calculate fill percentage (cap at 100)
  const fillPct = limit > 0 ? Math.min((used / limit) * 100, 100) : 100;

  return (
    <div className="usage-limit-overlay" onClick={handleOverlayClick}>
      <div className="usage-limit-card">
        <button className="usage-limit-close" onClick={onClose} aria-label="Close">
          &#x2715;
        </button>

        <h2 className="usage-limit-title">
          You've hit your {toolName} limit
        </h2>

        {/* Usage bar */}
        <div className="usage-limit-bar-container">
          <div className="usage-limit-bar-track">
            <div
              className="usage-limit-bar-fill"
              style={{ width: `${fillPct}%` }}
            />
          </div>
          <p className="usage-limit-bar-label">
            <strong>{used} / {limit}</strong> {unitLabel}{isMonthly ? ' used this month' : ' saved'}
            <span className="usage-limit-bar-separator"> · </span>
            <span
              className="usage-limit-view-usage"
              onClick={() => { onClose(); navigate('/usage'); }}
            >
              View Usage
            </span>
          </p>
        </div>

        {/* Tier comparison */}
        {upgradeTiers.length > 0 && (
          <div className="usage-limit-tiers">
            <p className="usage-limit-tiers-heading">Upgrade for more capacity</p>
            {upgradeTiers.map((t) => (
              <div key={t.name} className="usage-limit-tier-row">
                <span>
                  <span className="usage-limit-tier-name">{t.displayName}</span>
                  <span className="usage-limit-tier-price">
                    {t.price === 0 ? '' : ` ($${t.price}/mo)`}
                  </span>
                </span>
                <span className="usage-limit-tier-limit">
                  {t[limitKey]} {tierUnit}
                </span>
              </div>
            ))}
          </div>
        )}

        {/* CTAs */}
        <div className="usage-limit-buttons">
          <button className="usage-limit-button-primary" onClick={handleViewPlans}>
            View Plans
          </button>
          <button className="usage-limit-button-secondary" onClick={handleTryOtherTool}>
            Try the {otherToolName}
          </button>
        </div>
      </div>
    </div>
  );
}

export default UsageLimitModal;
